/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import {
  getEstadosGlobales,
} from 'api/catalogos/index';
import {
  getDeducciones,
} from 'api/nominas/deducciones/deducciones';
import {
  getTabuladoresNominalesDeducciones,
  postTabuladorNominalDeduccion,
  deleteTabuladorNominalDeduccion,
  patchTabuladorNominalDeduccion,
  getTabuladorNominalDeduccion,
} from 'api/nominas/tabuladores/tabuladoresNominalesDeducciones';
import { checkToken } from 'api/utils';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

export const tabuladorNominalDeduccionPermission = {
  permissionModel: 'tabuladornominaldeduccion',
  catalogsModels: [],
  tabsModels: [],
};

const TabuladorGeneralDeduccion = ({ permission, selected, estatusInactivo }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [deducciones, setDeducciones] = useState([]);
  const [deduccionesFiltered, setDeduccionesFiltered] = useState([]);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTabuladoresNominalesDeducciones({ tabulador_nominal: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _deducciones,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getDeducciones({ estados_globales: ESTATUS_AUTORIZADO }),
        getTabuladoresNominalesDeducciones({ tabulador_nominal: selected }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setDeducciones(_deducciones);
        setDeduccionesFiltered(
          deducciones.filter((e) => !data.some((val) => val.deduccion === e.id)),
        );
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setDeduccionesFiltered(
      deducciones.filter((e) => !data.some((val) => val.deduccion === e.id)),
    );
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _deduccion = await getTabuladorNominalDeduccion(match.id);
    setTimeout(() => {
      if (_deduccion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_deduccion));
          setDeduccionesFiltered(
            deducciones.filter((e) => !data.some((val) => {
              if (val.deduccion === _deduccion.deduccion) {
                return false;
              }
              return val.deduccion === e.id;
            })),
          );
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTabuladorNominalDeduccion(key, { ...values, tabulador_nominal: selected }) : postTabuladorNominalDeduccion({ ...values, tabulador_nominal: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTabuladorNominalDeduccion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Deducción',
      dataIndex: 'deduccion',
      key: 'deduccion',
      width: 200,
      render: (val) => {
        const _deduccion = deducciones.find((e) => e.id === val);
        return (_deduccion?.clave ? `${_deduccion?.clave} - ${_deduccion?.concepto}` : '');
      },
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          allowAdd={!estatusInactivo}
          allowEdit={!estatusInactivo}
          allowDelete={!estatusInactivo}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Deducción`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="40%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="deduccion"
                label="Deducción"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="concepto"
                  keyProp="clave"
                  keyLabelRender
                  dataSource={deduccionesFiltered}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Deducción ${deducciones.find((e) => e.id === form.getFieldValue('deduccion'))?.clave}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TabuladorGeneralDeduccion;
